import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Loader2, ArrowLeft } from "lucide-react";

const ACTION_COLORS = {
  login: "bg-slate-100 text-slate-700",
  create_user: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  update_user: "bg-blue-50 text-blue-700 border border-blue-200",
  set_status: "bg-amber-50 text-amber-700 border border-amber-200",
  reset_password: "bg-violet-50 text-violet-700 border border-violet-200",
  change_password: "bg-violet-50 text-violet-700 border border-violet-200",
  update_permissions: "bg-indigo-50 text-indigo-700 border border-indigo-200",
};

export default function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/audit-logs");
        setLogs(data);
      } catch (err) {
        toast.error(apiError(err.response?.data?.detail));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="space-y-6" data-testid="audit-log-page">
      <div>
        <NavLink to="/users" className="mb-1 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"><ArrowLeft className="h-4 w-4" />Back to users</NavLink>
        <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Audit Log</h2>
        <p className="mt-1 text-sm text-slate-500">A record of every sensitive action in the system.</p>
      </div>

      <Card className="p-4">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Time</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">User</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Action</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Module</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Detail</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((l) => (
                  <TableRow key={l.id} data-testid="audit-row">
                    <TableCell className="whitespace-nowrap text-slate-500">{new Date(l.timestamp).toLocaleString()}</TableCell>
                    <TableCell className="font-medium text-slate-800">{l.actor_name || l.actor_email}</TableCell>
                    <TableCell><Badge className={`hover:bg-inherit ${ACTION_COLORS[l.action] || "bg-slate-100 text-slate-700"}`}>{l.action}</Badge></TableCell>
                    <TableCell className="text-slate-600">{l.module}</TableCell>
                    <TableCell className="text-slate-600">{l.detail}</TableCell>
                  </TableRow>
                ))}
                {logs.length === 0 && (
                  <TableRow><TableCell colSpan={5} className="py-12 text-center text-slate-400">No audit entries yet</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
